import MobileAppBar from '@/components/molecules/MobileAppBar';
import DashboardTemplate from '@/components/templates/DashboardTemplate';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import searchIcon from '@/assets/icons/search.svg';
import CommitmentIcon from '@/assets/icons/commitment.svg';
import { Button } from '@/components/ui/button';
import { PlusIcon } from 'lucide-react';
import { format } from 'date-fns';
import CommitmentCalendar, { Commitment } from './CommitmentCalendar';
import CommitmentSidebar from './CommitmentSidebar';
import CommitmentList from './CommitmentList';
import { invoiceService } from '@/services/invoice.service';
import { Commitment as CommitmentType } from '@/services/types/invoice.types';
import { useLocation, useNavigate } from 'react-router-dom';
import { PAGE } from '@/routes';
import { useDeviceType } from '@/hooks/useDeviceType';
import { calendarMonthToApiDate, commitmentDateToCalendarKey } from '@/utils/commitmentCalendarDate';

function commitmentCalendarRow(c: CommitmentType): Commitment {
  return {
    id: c.id,
    title: c.title,
    budgetName: c.budgetName,
    date: new Date(c.date)
  };
}

const MOBILE_APP_BAR_HEIGHT = 56;

const CommitmentPage = () => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [commitments, setCommitments] = useState<CommitmentType[]>([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [showCompactCalendar, setShowCompactCalendar] = useState(false);
  const [loading, setLoading] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const persistedSelectedDate = location.state?.selectedDate as string | undefined;

  console.log('calendarMonth', calendarMonth.toISOString());

  useEffect(() => {
    if (persistedSelectedDate) {
      setSelectedDate(new Date(persistedSelectedDate));
      return;
    }
    if (window.matchMedia('(max-width: 767px)').matches) {
      setSelectedDate(new Date());
    }
  }, [persistedSelectedDate]);
  const { isMobileView } = useDeviceType();

  useEffect(() => {
    if (!isMobileView) {
      setShowCompactCalendar(false);
      return;
    }

    const scrollParent = calendarRef.current?.closest('main');
    if (!scrollParent || !calendarRef.current) return;

    const handleScroll = () => {
      if (!calendarRef.current) return;
      const calRect = calendarRef.current.getBoundingClientRect();
      const parentRect = scrollParent.getBoundingClientRect();
      setShowCompactCalendar(calRect.bottom < parentRect.top + MOBILE_APP_BAR_HEIGHT);
    };

    scrollParent.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollParent.removeEventListener('scroll', handleScroll);
  }, [isMobileView]);
  const variant = isMobileView ? 'default' : 'secondary';

  const handleCreateCommitment = (date?: Date) => {
    navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
      state: {
        from: 'commitment',
        ...(date ? { issueDate: date.toISOString() } : {})
      }
    });
  };

  const selectedDateCommitments = useMemo(() => {
    if (!selectedDate) return [];
    const key = format(selectedDate, 'yyyy-MM-dd');
    return commitments.filter((c) => commitmentDateToCalendarKey(c.date) === key).map(commitmentCalendarRow);
  }, [selectedDate, commitments]);

  const fetchCommitments = async () => {
    try {
      setLoading(true);
      setCommitments([]);
      const { data } = await invoiceService.getCommitments({
        date: calendarMonthToApiDate(calendarMonth)
      });
      setCommitments(data);
      // console.log(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCommitments();
  }, [calendarMonth]);

  const handleDateSelect = (date: Date) => {
    if (selectedDate && format(selectedDate, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')) {
      return;
    }
    setSelectedDate(date);
    navigate(location.pathname, {
      replace: true,
      state: { ...location.state, selectedDate: date.toISOString() }
    });
  };

  const showSidebar = !!selectedDate;

  return (
    <DashboardTemplate>
      <MobileAppBar title="Commitment" variant="primary" onRightIconClick={() => {}} rightIcon={searchIcon} />

      {/* Sticky compact calendar — mobile only, visible when full calendar scrolls out */}
      {showCompactCalendar && (
        <div className="md:hidden sticky top-[56px] z-10 bg-white border-b shadow-sm animate-slide-down">
          <CommitmentCalendar
            commitments={commitments.map(commitmentCalendarRow)}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            compact
            currentMonth={calendarMonth}
            onMonthChange={setCalendarMonth}
          />
        </div>
      )}

      <div className="flex w-full max-w-[1480px] mx-auto">
        {/* main */}
        <div className="bg-white flex-1 flex flex-col gap-4">
          <div className="flex-1 bg-white w-full md:rounded-[16px] p-0 pt-2 pb-4 md:p-4 md:border flex flex-col gap-4">
            <header className="hidden md:flex items-center justify-between border-b pb-2">
              <h2 className="flex items-center gap-2 text-2xl font-semibold">
                <img src={CommitmentIcon} alt="commitment" className="w-6 h-6" />
                <span>Commitment</span>
              </h2>

              <Button
                variant={'text'}
                onClick={() => handleCreateCommitment()}
                className="rounded-full hidden md:flex items-center font-semibold text-sm md:text-lg gap-1.5 px-6"
              >
                <PlusIcon className="w-5 h-5" />
                <span>Create an Commitment</span>
              </Button>
            </header>

            <div ref={calendarRef}>
              <CommitmentCalendar
                commitments={commitments.map(commitmentCalendarRow)}
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
                currentMonth={calendarMonth}
                onMonthChange={setCalendarMonth}
              />
            </div>

            {selectedDate && (
              <CommitmentList
                date={selectedDate}
                commitments={selectedDateCommitments}
                onCreateCommitment={() => handleCreateCommitment(selectedDate)}
                variant={variant}
                loading={loading}
              />
            )}
          </div>
        </div>

        {/* sidebar — desktop only */}
        <div
          className={`hidden md:block shrink-0 transition-all duration-300 ease-in-out ${
            showSidebar ? 'w-[360px] opacity-100 ml-6' : 'w-0 opacity-0 ml-0'
          }`}
        >
          <CommitmentSidebar
            date={selectedDate ?? new Date()}
            commitments={selectedDateCommitments}
            onClose={() => setSelectedDate(null)}
            onCreateCommitment={() => handleCreateCommitment(selectedDate ?? undefined)}
          />
        </div>
      </div>
    </DashboardTemplate>
  );
};

export default CommitmentPage;
